import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Table, Input, Button, Row, Pagination, Tooltip, Progress, Badge, message, Skeleton, Spin } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  TeamOutlined,
  CalendarOutlined,
  FilterOutlined,
  FolderOutlined,
  PlusOutlined,
  UserOutlined,
  CheckOutlined,
  SearchOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { withRouter, Link } from 'react-router-dom';
import { debounce } from 'lodash';
import useSearchParams from '../../../../../core/hooks/useSearchParams';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  PRIORITY_CSS_CLASS,
  STATUS_CONFIG,
  URGENCY_CONFIG,
  getProjectRowClassName,
  formatProjectDueDate,
} from '../../utils/taskTableUtils';
import ProjectBoardFilters from './ProjectBoardFilters';
import { text } from '../../configs/designTokens';
import '../../styles/index.scss';
import moment from 'moment';

const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
const searchShortcut = isMac ? '⌘K' : 'Ctrl+K';

// Skeleton Loading Component for Table
const ProjectTableSkeleton = ({ rows = 5 }) => (
  <div className="project-table-skeleton" role="status" aria-label="Loading projects">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="project-table-skeleton__row">
        {/* Checkbox */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--checkbox">
          <Skeleton.Avatar active size={20} shape="square" />
        </div>
        {/* Project: priority dot + status badge + name */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--name">
          <div className="project-table-skeleton__name-content">
            <Skeleton.Avatar active size={8} shape="circle" className="project-table-skeleton__priority-dot" />
            <Skeleton.Button active size="small" style={{ width: 70, height: 20, minWidth: 70 }} />
            <Skeleton.Input active size="small" style={{ width: '60%' }} />
          </div>
        </div>
        {/* Owner: avatar + name */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--owner">
          <Skeleton.Avatar active size={27} />
          <Skeleton.Input active size="small" style={{ width: 80 }} />
        </div>
        {/* Team: stacked avatars */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--team">
          <div className="project-table-skeleton__avatars">
            <Skeleton.Avatar active size={28} />
            <Skeleton.Avatar active size={28} />
            <Skeleton.Avatar active size={28} />
          </div>
        </div>
        {/* Timeline: dates + progress bar */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--timeline">
          <div className="project-table-skeleton__timeline-content">
            <Skeleton.Input active size="small" style={{ width: 100 }} />
            <div className="project-table-skeleton__timeline-bar">
              <Skeleton.Input active size="small" style={{ width: '100%', height: 4 }} />
            </div>
          </div>
        </div>
        {/* Progress: progress bar */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--progress">
          <div className="project-table-skeleton__progress-bar">
            <Skeleton.Input active size="small" style={{ width: '100%', height: 8 }} />
          </div>
        </div>
        {/* Actions: menu button */}
        <div className="project-table-skeleton__cell project-table-skeleton__cell--actions">
          <Skeleton.Avatar active size={24} shape="circle" />
        </div>
      </div>
    ))}
  </div>
);

// Skeleton Loading Component for Mobile Cards
const ProjectCardSkeleton = ({ count = 3 }) => (
  <div className="project-card-skeleton" role="status" aria-label="Loading projects">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="project-card-skeleton__card">
        <div className="project-card-skeleton__header">
          <Skeleton.Input active size="small" style={{ width: '70%' }} />
        </div>
        <div className="project-card-skeleton__meta">
          <Skeleton.Avatar active size={20} />
          <Skeleton.Input active size="small" style={{ width: 100 }} />
          <Skeleton.Input active size="small" style={{ width: 80 }} />
        </div>
        <div className="project-card-skeleton__footer">
          <Skeleton.Input active size="small" style={{ width: '60%' }} />
          <Skeleton.Button active size="small" style={{ width: 70 }} />
        </div>
      </div>
    ))}
  </div>
);

// Empty State Component
const ProjectEmptyState = ({ type, onAction, hasFilters }) => {
  const states = {
    noProjects: {
      icon: <FolderOutlined />,
      title: 'No projects yet',
      description: 'Projects help you organize related tasks and track progress toward your goals.',
      actionText: 'Create Your First Project',
    },
    noResults: {
      icon: <SearchOutlined />,
      title: 'No projects found',
      description: hasFilters
        ? 'Try adjusting your filters or search terms to find what you\'re looking for.'
        : 'No projects match your search criteria.',
      actionText: hasFilters ? 'Clear Filters' : null,
    },
    error: {
      icon: <InboxOutlined />,
      title: 'Unable to load projects',
      description: 'Something went wrong while loading your projects. Please try again.',
      actionText: 'Try Again',
    },
  };

  const state = states[type] || states.noProjects;

  return (
    <div className="project-empty-state" role="status">
      <div className="project-empty-state__icon">{state.icon}</div>
      <h3 className="project-empty-state__title">{state.title}</h3>
      <p className="project-empty-state__description">{state.description}</p>
      {state.actionText && onAction && (
        <Button
          type="primary"
          className="project-empty-state__action"
          onClick={onAction}
        >
          {state.actionText}
        </Button>
      )}
    </div>
  );
};

// Mobile Project Card Component
const ProjectCard = ({
  project,
  history,
  getProjectProgress,
}) => {
  const progress = getProjectProgress(project);
  const { isOverdue, daysOverdue, daysLeft, isToday, isDueSoon } = formatProjectDueDate(project.endDate, project.status);

  // Get status config for badge styling
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.NOT_STARTED;

  // Check if project is completed/canceled
  const isCompleted = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE'].includes(project.status?.toUpperCase());

  // Build card classes with priority for left border
  const priority = project.priority || 'LOW';
  const priorityClass = PRIORITY_CSS_CLASS[priority.toUpperCase()] || 'low';
  const priorityConfig = URGENCY_CONFIG[priority.toUpperCase()] || URGENCY_CONFIG.LOW;
  const cardClasses = [
    'project-card',
    `project-card--priority-${priorityClass}`,
    isOverdue ? 'project-card--overdue' : '',
    isCompleted ? 'project-card--completed' : '',
  ].filter(Boolean).join(' ');

  const handleCardClick = () => {
    history.push(`/tasks/projects/details/${project.id}`);
  };

  return (
    <div className={cardClasses} onClick={handleCardClick}>
      {/* Priority indicator with tooltip */}
      <Tooltip title={priorityConfig.label} placement="left">
        <span className={`project-card__priority-indicator project-card__priority-indicator--${priorityConfig.cssClass}`} />
      </Tooltip>

      <div className="project-card__header">
        <div className="project-card__title-row">
          <span className={`project-card__inline-status project-card__inline-status--${statusConfig.cssClass}`}>
            {statusConfig.label}
          </span>
          <span className="project-card__name">{project.name}</span>
        </div>
      </div>

      <div className="project-card__meta">
        <div className="project-card__meta-item">
          <UserOutlined />
          <span>{project.owner?.name || 'Unassigned'}</span>
        </div>
        <div className="project-card__meta-item">
          <CalendarOutlined />
          <span>
            {project.startDate ? moment(project.startDate).format('MMM D') : '-'}
            {' - '}
            {project.endDate ? moment(project.endDate).format('MMM D') : '-'}
          </span>
        </div>
        <div className="project-card__meta-item">
          <TeamOutlined />
          <span>{project.team?.length || 0} members</span>
        </div>
      </div>

      <div className="project-card__footer">
        <div className="project-card__progress">
          <Progress
            percent={isCompleted ? 100 : progress}
            size="small"
            showInfo={false}
            strokeColor={isCompleted ? STATUS_CONFIG.COMPLETED.color : undefined}
            status={isCompleted ? 'success' : undefined}
          />
          <span className="project-card__progress-text">
            {isCompleted ? <CheckOutlined style={{ color: STATUS_CONFIG.COMPLETED.color }} /> : `${progress}%`}
          </span>
        </div>

        {/* Overdue/Due pill (same style as Tasks) - hide for completed */}
        {!isCompleted && isOverdue && (
          <span className="project-card__overdue-pill">
            <ExclamationCircleOutlined />
            {daysOverdue}d overdue
          </span>
        )}
        {!isCompleted && !isOverdue && isToday && (
          <span className="project-card__due-today">
            Due today
          </span>
        )}
        {!isCompleted && !isOverdue && !isToday && isDueSoon && (
          <span className="project-card__due-soon">
            {daysLeft}d left
          </span>
        )}
      </div>
    </div>
  );
};

const ProjectBoard = ({ history }) => {
  const { width: windowWidth, isMobile, isTablet, isDesktop } = useResponsiveTask();
  const {
    currentUser,
    fetchProjectsPaginated,
    projects,
    projectBoardPagination,
    setProjectBoardPagination,
    projectsLoading,
    fetchTeamMemberUsers,
    teamMemberUsers,
    teamMemberUsersLoading,
    fetchOwnerUsers,
    ownerUsers,
    ownerUsersLoading,
  } = useProjectContext();

  const [searchParams, setSearchParams] = useSearchParams({
    name: '',
    teamMembers: '',
    owner: '',
    startDate: '',
    endDate: '',
    status: '',
  });
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(moment().valueOf());
  const [showFilters, setShowFilters] = useState(false);
  const [loadedPages, setLoadedPages] = useState(1);
  const [accumulatedProjects, setAccumulatedProjects] = useState([]);
  const loadingMoreRef = useRef(false);
  const lastProcessedProjectsRef = useRef(null);

  // Reference for search input
  const searchInputRef = useRef(null);

  // Accumulate projects for mobile infinite scroll
  useEffect(() => {
    if (!isMobile) {
      // Desktop: just use projects directly
      setAccumulatedProjects(projects);
      return;
    }

    // Skip if projects haven't changed (same reference)
    if (projects === lastProcessedProjectsRef.current) {
      return;
    }

    // Skip empty arrays (loading state)
    if (!projects || projects.length === 0) {
      return;
    }

    // Mark these projects as processed
    lastProcessedProjectsRef.current = projects;

    // Determine if this is a reset or load more by checking pagination
    if (projectBoardPagination.current === 1) {
      // Reset - replace all
      setAccumulatedProjects([...projects]);
    } else {
      // Load more - append (with deduplication)
      setAccumulatedProjects((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newProjects = projects.filter((p) => !existingIds.has(p.id));
        if (newProjects.length === 0) {
          return prev; // No new projects, don't update
        }
        return [...prev, ...newProjects];
      });
    }

    // Reset loading flag when new data arrives
    loadingMoreRef.current = false;
  }, [projects, isMobile, projectBoardPagination]);

  // Use accumulated projects for mobile, regular projects for desktop
  const displayProjects = isMobile ? accumulatedProjects : projects;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Escape in search input clears it
        if (e.key === 'Escape' && e.target === searchInputRef.current?.input) {
          setSearchParams({ ...searchParams, name: '' });
          e.target.blur();
        }
        return;
      }

      // Cmd/Ctrl + K - Focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Cmd/Ctrl + N - New project
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        history.push('/tasks/projects/new');
      }

      // F - Toggle filters (when not in input)
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShowFilters((prev) => !prev);
      }

      // Escape - Close filters
      if (e.key === 'Escape') {
        if (showFilters) {
          setShowFilters(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchParams, history, showFilters, projects, setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchParams.teamMembers ||
      searchParams.owner ||
      searchParams.startDate ||
      searchParams.status
    );
  }, [searchParams]);

  const debouncedFetchRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    debouncedFetchRef.current = debounce(
      ({ skip, first, sortField = null, sortOrder = 'updatedAt_DESC', name = '', teamMembers = '', owner = '', startDate = '', endDate = '', status = '' }) => {
        const orderBy =
          sortOrder === 'updatedAt_DESC'
            ? 'updatedAt_DESC'
            : sortField
              ? `${sortField}_${sortOrder}`
              : 'updatedAt_DESC';

        const teamMembersArray = teamMembers ? teamMembers.split(',') : [];
        const ownerArray = owner ? owner.split(',') : [];
        const statusArray = status ? status.split(',') : [];

        fetchProjectsPaginated({
          variables: {
            where: {
              AND: [
                { name_contains: name },
                ...(teamMembersArray.length
                  ? [{ team_some: { id_in: teamMembersArray } }]
                  : []),
                ...(ownerArray.length
                  ? [{ owner: { id_in: ownerArray } }]
                  : []),
                ...(startDate && endDate
                  ? [{
                      startDate_gte: startDate,
                      endDate_lte: endDate,
                    }]
                  : []),
                ...(statusArray.length
                  ? [{ status_in: statusArray }]
                  : []),
                {
                  OR: [
                    {
                      owner: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                            ],
                          },
                        },
                      },
                    },
                  ],
                },
              ],
            },
            orderBy,
            skip,
            first,
          },
        });
      },
      500
    );
  }, [fetchProjectsPaginated, currentUser.role.org.id]);

  const handleFetchProjectsPaginated = useCallback(
    (params) => debouncedFetchRef.current(params),
    []
  );

  const handlePaginationChange = (params) => {
    const updatedPagination = {
      ...projectBoardPagination,
      current: params.page || projectBoardPagination.current,
      pageSize: params.pageSize || projectBoardPagination.pageSize,
    };

    const sortOrder =
      params.sortOrder === 'ascend'
        ? 'ASC'
        : params.sortOrder === 'descend'
          ? 'DESC'
          : 'updatedAt_DESC';

    const sortField =
      sortOrder === 'updatedAt_DESC' ? null : params.sortField || projectBoardPagination.sortField;

    setProjectBoardPagination(updatedPagination);

    handleFetchProjectsPaginated({
      skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
      first: updatedPagination.pageSize,
      sortField,
      sortOrder,
      ...searchParams,
    });
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const sortField = sorter.field || null;
    const sortOrder = sorter.order || null;

    handlePaginationChange({
      page: 1,
      sortField,
      sortOrder,
    });
  };

  const handlePaginationSizeChange = (_current, pageSize) => {
    handlePaginationChange({
      page: 1,
      pageSize,
    });
  };

  const itemRender = (_page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button icon={<LeftOutlined />} type="text">
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  // Infinite scroll for mobile - only true if we have some data and there's more to load
  const hasMorePages = accumulatedProjects.length > 0 && projectBoardPagination.total > accumulatedProjects.length;

  const loadMoreProjects = useCallback(() => {
    if (!projectsLoading && hasMorePages && !loadingMoreRef.current) {
      loadingMoreRef.current = true;
      handlePaginationChange({ page: projectBoardPagination.current + 1 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsLoading, hasMorePages, projectBoardPagination.current]);

  useEffect(() => {
    if (!isMobile || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !projectsLoading && !loadingMoreRef.current) {
          loadMoreProjects();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isMobile, hasMorePages, projectsLoading, loadMoreProjects]);

  useEffect(() => {
    handleFetchProjectsPaginated({
      skip: (projectBoardPagination.current - 1) * projectBoardPagination.pageSize || 0,
      first: projectBoardPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearchChange = (name) => {
    const updatedFilters = { ...searchParams, name };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTeamMembersFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      teamMembers: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleOwnerFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      owner: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTimelineFilter = (dates) => {
    const updatedFilters = {
      ...searchParams,
      startDate: dates && dates.length === 2 ? dates[0].format('YYYY-MM-DD') : '',
      endDate: dates && dates.length === 2 ? dates[1].format('YYYY-MM-DD') : '',
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const hasFilterChanges = useMemo(() => {
    const savedState = localStorage.getItem(`projectState_${currentUser.id}`);
    if (!savedState) {
      return true;
    }

    try {
      const {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      } = JSON.parse(savedState);

      const currentState = {
        searchParams,
        projectBoardPagination,
      };

      const savedStateObj = {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      };

      return JSON.stringify(currentState) !== JSON.stringify(savedStateObj);
    } catch (error) {
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, projectBoardPagination, currentUser.id, lastSavedTimestamp]);

  const handleSaveFilters = () => {
    if (!hasFilterChanges) {
      message.destroy();
      message.info('No filter changes detected.');
      return;
    }

    const stateToSave = {
      searchParams: { ...searchParams },
      projectBoardPagination,
    };

    localStorage.setItem(`projectState_${currentUser.id}`, JSON.stringify(stateToSave));
    setLastSavedTimestamp(moment().valueOf());
    message.destroy();
    message.success('Filters saved successfully!');
  };

  const handleLoadFilters = () => {
    const savedState = localStorage.getItem(`projectState_${currentUser.id}`);
    if (!savedState) {
      message.destroy();
      message.info('No saved filters found.');
      return;
    }

    try {
      const {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      } = JSON.parse(savedState);

      setSearchParams(savedSearchParams);
      setProjectBoardPagination(savedPagination);

      message.destroy();
      message.success('Filters loaded successfully!');
    } catch (error) {
      message.error('Failed to load saved filters.');
    }
  };

  const handleClearFilters = () => {
    const clearedParams = {
      name: '',
      teamMembers: '',
      owner: '',
      startDate: '',
      endDate: '',
      status: '',
    };
    setSearchParams(clearedParams);
    setProjectBoardPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  const handleFetchTeamMemberUsers = useCallback(
    debounce((name = '') => {
      fetchTeamMemberUsers({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              { assignedTeamProjects_some: {} },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                  org: {
                    OR: [
                      { id: currentUser.role.org.id },
                      { parentOrg_some: { id: currentUser.role.org.id } },
                    ],
                  },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchTeamMemberUsers]
  );

  const handleFetchOwnerUsers = useCallback(
    debounce((name = '') => {
      fetchOwnerUsers({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              { createdProjects_some: {} },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                  org: {
                    OR: [
                      { id: currentUser.role.org.id },
                      { parentOrg_some: { id: currentUser.role.org.id } },
                    ],
                  },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchOwnerUsers]
  );

  useEffect(() => {
    if (searchParams.teamMembers === '') {
      handleFetchTeamMemberUsers();
    }
  }, [handleFetchTeamMemberUsers, searchParams.teamMembers]);

  useEffect(() => {
    if (searchParams.owner === '') {
      handleFetchOwnerUsers();
    }
  }, [handleFetchOwnerUsers, searchParams.owner]);

  const getProjectProgress = (project) => {
    if (!project.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(
      (task) => task.status === 'COMPLETED' || task.status === 'DONE'
    ).length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };


  const columns = [
    {
      title: <span className="project-board__column-header">Project</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => {
        const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG.NOT_STARTED;
        const priorityConfig = URGENCY_CONFIG[record.priority] || URGENCY_CONFIG.LOW;
        const { isOverdue, daysOverdue } = formatProjectDueDate(record.endDate, record.status);

        return (
          <div className="project-table__name-with-status">
            {/* Priority indicator with tooltip */}
            <Tooltip title={priorityConfig.label} placement="left">
              <span className={`project-table__priority-indicator project-table__priority-indicator--${priorityConfig.cssClass}`} />
            </Tooltip>

            {/* Inline status badge (same style as Tasks) */}
            <span className={`project-table__inline-status project-table__inline-status--${statusConfig.cssClass}`}>
              {statusConfig.label}
            </span>
            <div className="project-table__name-content">
              <Link
                to={`/tasks/projects/details/${record.id}`}
                className="project-table__link"
              >
                {text}
              </Link>
              {/* Overdue pill shown inline */}
              {isOverdue && (
                <span className="project-table__overdue-pill">
                  <ExclamationCircleOutlined />
                  {daysOverdue}d overdue
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <span className="project-board__column-header">Owner</span>,
      dataIndex: 'owner',
      key: 'owner',
      width: 220,
      render: (owner) => (
        <div className="project-board__owner-cell">
          {owner ? (
            <>
              <div className="project-board__avatar-wrapper">
                <AvatarS3
                  imgKey={owner.avatar && owner.avatar.key}
                  username={owner.name || '-'}
                  avatarProps={{ size: 27 }}
                />
              </div>
              <span className="project-board__owner-name">
                {owner.name}
              </span>
            </>
          ) : (
            'N/A'
          )}
        </div>
      ),
    },
    {
      title: <span className="project-board__column-header">Team</span>,
      dataIndex: 'team',
      key: 'team',
      width: 120,
      render: (team) => {
        if (!team || team.length === 0) return '-';

        const maxVisible = 3;
        const visibleMembers = team.slice(0, maxVisible);
        const remainingCount = team.length - maxVisible;

        return (
          <div className="project-team-stack">
            <div className="project-team-stack__avatars">
              {visibleMembers.map((member, index) => (
                <Tooltip key={member.id} title={member.name || member.username}>
                  <div className="project-team-stack__avatar">
                    <AvatarS3
                      imgKey={member.avatar && member.avatar.key}
                      username={member.name || member.username || '-'}
                      avatarProps={{ size: 28 }}
                    />
                  </div>
                </Tooltip>
              ))}
              {remainingCount > 0 && (
                <Tooltip
                  title={team.slice(maxVisible).map(m => m.name || m.username).join(', ')}
                >
                  <div className="project-team-stack__overflow">
                    +{remainingCount}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <span className="project-board__column-header">Timeline</span>,
      dataIndex: 'timeline',
      key: 'timeline',
      width: 150,
      render: (_, record) => {
        if (!record.startDate && !record.endDate) return <span className="project-table__no-date">—</span>;

        const start = record.startDate ? moment(record.startDate).format('MMM D') : '-';
        const end = record.endDate ? moment(record.endDate).format('MMM D') : '-';

        const today = moment();
        const startDate = record.startDate ? moment(record.startDate) : null;
        const endDate = record.endDate ? moment(record.endDate) : null;
        const daysLeft = endDate ? endDate.diff(today, 'days') : null;

        // Check if project is completed/canceled
        const isCompleted = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE'].includes(record.status?.toUpperCase());

        // Calculate timeline progress percentage
        let timelineProgress = 0;
        let timelineStatus = 'on-track';

        if (isCompleted) {
          // Completed projects always show green at 100%
          timelineStatus = 'on-track';
          timelineProgress = 100;
        } else if (startDate && endDate) {
          const totalDuration = endDate.diff(startDate, 'days');
          const elapsed = today.diff(startDate, 'days');
          if (totalDuration > 0) {
            timelineProgress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
          }
          if (daysLeft < 0) {
            timelineStatus = 'overdue';
            timelineProgress = 100;
          } else if (daysLeft <= 7) {
            timelineStatus = 'warning';
          }
        }

        return (
          <div className="project-board__timeline-cell">
            <div className="project-board__timeline-dates">
              <div className="project-board__timeline-date">
                {start} - {end}
              </div>
              {/* Visual timeline bar */}
              <div className="project-timeline-bar">
                <div className="project-timeline-bar__track">
                  <div
                    className={`project-timeline-bar__fill project-timeline-bar__fill--${timelineStatus}`}
                    style={{ width: `${timelineProgress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: <span className="project-board__column-header">Progress</span>,
      dataIndex: 'progress',
      key: 'progress',
      width: 220,
      render: (_, record) => {
        const progress = getProjectProgress(record);
        const isCompleted = ['COMPLETED', 'CANCELED', 'CANCELLED', 'DONE'].includes(record.status?.toUpperCase());

        // For completed projects, show 100% green with checkmark
        const displayPercent = isCompleted ? 100 : progress;
        const strokeColor = isCompleted ? STATUS_CONFIG.COMPLETED.color : undefined; // Green for completed

        return (
          <div className="project-board__progress-cell">
            <Progress
              percent={displayPercent}
              size="small"
              strokeColor={strokeColor}
              status={isCompleted ? 'success' : undefined}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="project-board__container" role="main" aria-label="Project Board">
      {/* Screen reader only: Keyboard shortcuts help */}
      <div className="sr-only" aria-live="polite">
        Press Ctrl+K or Cmd+K to search. Press F to toggle filters. Press Escape to clear selection.
      </div>

      {/* Mobile: Search + Filter button row */}
      {isMobile ? (
        <div className="project-board__mobile-search-row">
          <Input
            ref={searchInputRef}
            placeholder="Search projects..."
            value={searchParams.name || ''}
            onChange={(e) => {
              handleSearchChange(e.target.value);
            }}
            className="project-board__search-mobile"
            aria-label="Search projects"
            allowClear
            prefix={<SearchOutlined style={{ color: text.muted }} />}
          />
          <button
            className={`project-board__filter-btn-mobile ${showFilters ? 'project-board__filter-btn-mobile--active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterOutlined />
            {hasActiveFilters && (
              <span className="project-board__filter-badge-dot" />
            )}
          </button>
        </div>
      ) : (
        <Row className="project-board__search-row">
          {/* Search Input - takes available space */}
          <Input
            ref={searchInputRef}
            placeholder={`Search projects (${searchShortcut})`}
            value={searchParams.name || ''}
            onChange={(e) => {
              handleSearchChange(e.target.value);
            }}
            className="project-board__search-input"
            aria-label="Search projects"
            prefix={<SearchOutlined className="project-board__search-icon" />}
            allowClear
          />

          {/* Filters Button */}
          <Button
            className={`task-filters__toggle ${hasActiveFilters ? 'task-filters__toggle--active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            icon={<FilterOutlined />}
          >
            Filters
            {hasActiveFilters && (
              <Badge
                count={
                  (searchParams.teamMembers ? 1 : 0) +
                  (searchParams.owner ? 1 : 0) +
                  (searchParams.startDate ? 1 : 0) +
                  (searchParams.status ? 1 : 0)
                }
                className="task-filters__badge"
              />
            )}
          </Button>

          {/* Create Project Button */}
          <Button
            type="primary"
            onClick={() => {
              history.push(`/tasks/projects/new`);
            }}
            className="project-board__create-btn"
          >
            Create Project
          </Button>
        </Row>
      )}

      <ProjectBoardFilters
        windowWidth={windowWidth}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        searchParams={searchParams}
        handleTeamMembersFilter={handleTeamMembersFilter}
        handleOwnerFilter={handleOwnerFilter}
        handleTimelineFilter={handleTimelineFilter}
        handleStatusFilter={handleStatusFilter}
        teamMemberUsers={teamMemberUsers}
        ownerUsers={ownerUsers}
        teamMemberUsersLoading={teamMemberUsersLoading}
        ownerUsersLoading={ownerUsersLoading}
        handleFetchTeamMemberUsers={handleFetchTeamMemberUsers}
        handleFetchOwnerUsers={handleFetchOwnerUsers}
        onClearFilters={handleClearFilters}
        onSaveFilters={handleSaveFilters}
        onLoadFilters={handleLoadFilters}
      />

      {/* Desktop/Tablet Table View */}
      {!isMobile && (
        <>
          {projectsLoading ? (
            <div className="project-board__table-wrapper">
              <ProjectTableSkeleton rows={projectBoardPagination.pageSize > 10 ? 10 : projectBoardPagination.pageSize} />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={displayProjects}
              pagination={false}
              scroll={{ x: 1100 }}
              className="task-table project-board__table"
              onChange={handleTableChange}
              rowKey="id"
              rowClassName={(record) => {
                return getProjectRowClassName(record);
              }}
              onRow={(record) => ({
                onClick: (e) => {
                  // Don't navigate if clicking on a link (which handles its own navigation), checkbox, or action button
                  if (
                    e.target.closest('a') ||
                    e.target.closest('.ant-checkbox-wrapper') ||
                    e.target.closest('.ant-dropdown-trigger')
                  ) {
                    return;
                  }
                  history.push(`/tasks/projects/details/${record.id}`);
                },
              })}
              locale={{
                emptyText: (
                  <ProjectEmptyState
                    type={hasActiveFilters || searchParams.name ? 'noResults' : 'noProjects'}
                    hasFilters={hasActiveFilters}
                    onAction={
                      hasActiveFilters || searchParams.name
                        ? handleClearFilters
                        : () => history.push('/tasks/projects/new')
                    }
                  />
                ),
              }}
            />
          )}
          <div className="project-pagination-enhanced">
            <div className="project-pagination-enhanced__left">
              <span className="project-pagination-enhanced__summary">
                {projectBoardPagination.total > 0 ? (
                  <>
                    Showing <strong>{((projectBoardPagination.current - 1) * projectBoardPagination.pageSize) + 1}</strong>
                    {' - '}
                    <strong>{Math.min(projectBoardPagination.current * projectBoardPagination.pageSize, projectBoardPagination.total)}</strong>
                    {' of '}
                    <strong>{projectBoardPagination.total}</strong> projects
                  </>
                ) : (
                  'No projects found'
                )}
              </span>
            </div>

            <div className="project-pagination-enhanced__center">
              <Pagination
                current={projectBoardPagination.current}
                pageSize={projectBoardPagination.pageSize}
                total={projectBoardPagination.total}
                showSizeChanger={false}
                hideOnSinglePage={false}
                onChange={(page) => handlePaginationChange({ page })}
                itemRender={itemRender}
                className="project-pagination-enhanced__nav"
              />
            </div>

            <div className="project-pagination-enhanced__right">
              <span className="project-pagination-enhanced__size-label">Show:</span>
              <div className="project-pagination-enhanced__size-options">
                {[10, 25, 50, 100].map((size) => (
                  <button
                    key={size}
                    className={`project-pagination-enhanced__size-btn ${projectBoardPagination.pageSize === size ? 'project-pagination-enhanced__size-btn--active' : ''}`}
                    onClick={() => handlePaginationSizeChange(projectBoardPagination.current, size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div className="project-board__cards">
          {projectsLoading && accumulatedProjects.length === 0 ? (
            <ProjectCardSkeleton count={5} />
          ) : displayProjects.length === 0 ? (
            <ProjectEmptyState
              type={hasActiveFilters || searchParams.name ? 'noResults' : 'noProjects'}
              hasFilters={hasActiveFilters}
              onAction={
                hasActiveFilters || searchParams.name
                  ? handleClearFilters
                  : () => history.push('/tasks/projects/new')
              }
            />
          ) : (
            <>
              {displayProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  history={history}
                  getProjectProgress={getProjectProgress}
                />
              ))}

              {/* Mobile Infinite Scroll */}
              <div className="project-mobile-pagination">
                {/* Progress indicator */}
                <div className="project-mobile-pagination__progress">
                  <div className="project-mobile-pagination__progress-bar">
                    <div
                      className="project-mobile-pagination__progress-fill"
                      style={{
                        width: `${Math.min(100, (accumulatedProjects.length / projectBoardPagination.total) * 100)}%`
                      }}
                    />
                  </div>
                  <span className="project-mobile-pagination__progress-text">
                    {accumulatedProjects.length} of {projectBoardPagination.total}
                  </span>
                </div>

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="project-mobile-pagination__sentinel">
                  {projectsLoading && hasMorePages && (
                    <div className="project-mobile-pagination__loading">
                      <Spin indicator={<LoadingOutlined spin />} size="small" />
                      <span>Loading more...</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          className="project-board__fab"
          onClick={() => history.push('/tasks/projects/new')}
          aria-label="Create new project"
        />
      )}

    </div>
  );
};

export default withRouter(ProjectBoard);
